import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ImapTariffsList } from 'reactApp/appHelpers/featuresHelpers/features/ImapSubscription';
import { mobileTariffsModalEnabled } from 'reactApp/appHelpers/featuresHelpers/features/mobileTariffsModalEnabled';
import { skipNoTariffDialogForAnon } from 'reactApp/appHelpers/featuresHelpers/features/skipNoTariffDialogForAnon';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { openBuyDisableAds, openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendWebviewAnalytics } from 'reactApp/sections/MobileQuotaTariffsLanding/MobileQuotaTariffsLanding.helpers';
import { EPostMessageBuy } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.types';
import { useTrialFromQueryReplacementForPaidUser } from 'reactApp/sections/QuotaLanding/hooks/useTrialFromQueryReplacementForPaidUser';
import { getQuotaBillingSource, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { revealPlatformPopup } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import { showNoTariffDialog } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import type { Product } from 'reactApp/types/Billing';
import { useDisableAdsAnalytics } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsAnalytics';
import { useDisableAdsTitle } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsTitle';
import { sendAdmitadProduct } from 'reactApp/utils/admitadHelper';
import opener from 'reactApp/utils/opener';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';
import { sendPostMessage } from 'reactApp/utils/windowHelper';

interface MobileInfo {
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isMobilePayAvailable?: boolean;
}

type ProductFromQueryAgs = {
    mobileInfo?: MobileInfo;
    forceMobile?: boolean;
    availableAdmitad?: boolean;
};

function goBack(backUrl: string) {
    if (backUrl) {
        opener(backUrl, true);
    }
}

/** https://jira.vk.team/browse/CLOUDWEB-20384 */
function tryParseFromBrokenParam(paramName: string) {
    const searchString = window.location.search;
    const splited = searchString.split('&');
    const brokenPart = splited.find((part) => part.includes(paramName));
    if (brokenPart) {
        const [_, paramValue] = brokenPart.split(`${paramName}=`);
        return paramValue;
    }
    return;
}

export const useProductFromQuery = (props?: ProductFromQueryAgs) => {
    const { mobileInfo = {}, forceMobile = false, availableAdmitad = false } = props || {};
    const dispatch = useDispatch();
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const isTariffPaidRef = useRef(false);
    const isMobile = useSelector(QuotaLandingSelectors.isMobile) || forceMobile;
    const queryParams = getQueryParams();

    let productIdFromQuery = queryParams?.productId || queryParams?.tariffId;
    if (!productIdFromQuery) {
        productIdFromQuery = tryParseFromBrokenParam('productId') || tryParseFromBrokenParam('tariffId');
    }

    const productsState = useSelector(ProductsSelectors.getLifeCycleState);
    const titleFromQuery = useDisableAdsTitle(queryParams?.title);
    const analyticFromQuery = useDisableAdsAnalytics(queryParams?.analyticsMap);
    const { productFromQuery, title } = useTrialFromQueryReplacementForPaidUser({ productIdFromQuery });
    const isFromAction = queryParams?.action === 'request-payment';
    const id = isFromAction ? productFromQuery?.id : '';
    const isFromActionAndLoaded = isFromAction && productsState.isLoaded;

    /* tempexp_20582-start */
    let successMessage;
    if (id && ImapTariffsList.includes(id)) {
        successMessage = 'Теперь можно создавать пароли для внешних приложений';
    }
    /* tempexp_20582-end */

    useEffect(() => {
        if (isFromAction && isAnonymous) {
            dispatch(
                authPopup({
                    closable: true,
                    loginRequest: true,
                    successPage: window.location.href,
                })
            );
            sendTmrGoal({ id: TMR3473039, goal: 'login-view' });
        }

        if (!isFromActionAndLoaded) {
            return;
        }

        const { tariffs, activeTab, backUrl } = queryParams || {};
        const canShowModal = Boolean(tariffs && (!isMobile || mobileTariffsModalEnabled));

        if (canShowModal) {
            dispatch(
                openBuyDisableAds({
                    isFrame: false,
                    paySource: analyticFromQuery.paySource,
                    actionShow: analyticFromQuery.actionShow,
                    actionClick: analyticFromQuery.actionClick,
                    name: analyticFromQuery.name,
                    activeTab,
                    title: titleFromQuery,
                    tariffsNumber: tariffs,
                    onBuyClick: (product: Product) => {
                        let isSuccess = false;
                        dispatch(
                            openTariffBuy({
                                productId: product.id,
                                isMobile,
                                paySource: analyticFromQuery.paySource,
                                onSuccess: () => {
                                    isSuccess = true;
                                    closePopupHelper(popupNames.BUY_DISABLE_ADS);
                                },
                                onClose: () => {
                                    if (isSuccess) {
                                        goBack(backUrl);
                                    }
                                },
                            })
                        );
                    },
                })
            );
            return;
        }

        const skipNoTariffForAnon = skipNoTariffDialogForAnon && isAnonymous && isFromAction;
        if (!productFromQuery?.available || !id) {
            sendQuotaGa({
                action: 'tariff-unavailable',
                label: 'show',
                tariff: productIdFromQuery || id,
                place: 'landing',
            });
            if (!skipNoTariffForAnon) {
                showNoTariffDialog();
            }
            return;
        }

        const { isAndroidPromo, isIOSPromo, isMobilePayAvailable } = mobileInfo;

        if (mobileInfo && isMobile && (isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
            revealPlatformPopup(id, Boolean(isIOSPromo), Boolean(isAndroidPromo));
            return;
        }

        let isSuccess = false;

        const onSuccess = () => {
            isSuccess = true;
            if (availableAdmitad) {
                sendAdmitadProduct(productFromQuery);
            }

            const price = productFromQuery?.hasTrial ? 0 : productFromQuery?.discountPrice || productFromQuery?.price || 0;

            sendQuotaGa({ action: 'tariff', label: 'buy', tariff: id, amount: price });
            sendTmrGoal({ id: TMR3473039, goal: 'tariff-buy' });
            sendWebviewAnalytics('payok', Boolean(isMobilePayAvailable));

            if (isMobile) {
                sendPostMessage({
                    paymentResult: EPostMessageBuy.SUCCESS,
                    productId: id,
                    price,
                    family: productFromQuery.isFamily,
                });
            }
            isTariffPaidRef.current = true;
        };

        const onClose = () => {
            if (isSuccess) {
                goBack(backUrl);
            }
        };

        dispatch(
            openTariffBuy({
                productId: id,
                title,
                source: getQuotaBillingSource(),
                onSuccess,
                onClose,
                isMobile,
                paySource: 'query',
                successMessage,
            })
        );
    }, [id, isFromActionAndLoaded]);
};
